import React, { useEffect, useState } from 'react';
import { vehiclesApi, Vehicle } from '@/services/api/vehicles';
import { Maintenance } from '@/types/common';
import { extractApiErrorMessage, getUserEmail } from '@/utils';
import { showErrorToast } from '@/components/toast/globalToast';
import { ReturnVehicleModal } from '@/pages/vehicles/components/ReturnVehicle';
import {
  requestPermSwap,
  RequestPermSwapRequest,
} from '@/services/api/maintenances';

interface MaintenanceReturnVehicleModalProps {
  isOpen: boolean;
  maintenance?: Maintenance | null;
  maintenanceId: string;
  vehicleId?: string | number;
  replacementVehicleId?: string | null;
  replacementVehicleData?: RequestPermSwapRequest['newVehicle'] | null;
  onClose: () => void;
  onSuccess: () => void;
  /** Called when returnCondition is MINOR_DAMAGE — captures payload instead of calling API */
  onReturnDataCapture?: (payload: RequestPermSwapRequest) => void;
}

const MaintenanceReturnVehicleModal: React.FC<
  MaintenanceReturnVehicleModalProps
> = ({
  isOpen,
  maintenance,
  maintenanceId,
  vehicleId,
  replacementVehicleId,
  replacementVehicleData,
  onClose,
  onSuccess,
  onReturnDataCapture,
}) => {
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);

  useEffect(() => {
    const fetchVehicle = async () => {
      if (!vehicleId) return;
      try {
        const response = await vehiclesApi.getVehicle(String(vehicleId));
        const payload = response as unknown as {
          vehicle?: Vehicle;
          data?: Vehicle | { data?: Vehicle };
        };
        const vehicleData =
          payload.vehicle ||
          (payload.data as Vehicle) ||
          (payload.data as { data?: Vehicle } | undefined)?.data ||
          null;
        setVehicle(vehicleData);
      } catch (error) {
        showErrorToast(
          'Error',
          extractApiErrorMessage(error, 'Failed to load vehicle detail')
        );
        setVehicle(null);
        onClose();
      }
    };

    if (isOpen) {
      setVehicle(null);
      fetchVehicle();
    }
  }, [isOpen, vehicleId, onClose]);

  if (!isOpen) return null;

  const handleConfirmReturn = async (
    returnVehicle: RequestPermSwapRequest['returnVehicle']
  ) => {
    if (!replacementVehicleId && !replacementVehicleData) {
      showErrorToast('Error', 'Replacement vehicle is required');
      return;
    }

    const payload: RequestPermSwapRequest = {
      newVehicleID: replacementVehicleId || undefined,
      newVehicle: replacementVehicleData || undefined,
      returnVehicle,
      updatedBy: getUserEmail(),
    };

    if (
      (maintenance as any)?.linkedIncidentType === 'MINOR_DAMAGE' &&
      onReturnDataCapture
    ) {
      // When return condition is MINOR_DAMAGE, defer the API call
      // and let the caller handle it (e.g., open return handover modal first)
      onReturnDataCapture(payload);
      return;
    }

    await requestPermSwap(maintenanceId, payload);
  };

  return (
    <ReturnVehicleModal
      isOpen={isOpen}
      vehicle={vehicle}
      isEditMode={false}
      onClose={onClose}
      onSuccess={onSuccess}
      onConfirmReturn={handleConfirmReturn}
      allowedReturnReasons={['VEHICLE_SWAP', 'OTHER']}
    />
  );
};

export default MaintenanceReturnVehicleModal;
